import { createFileRoute } from "@tanstack/react-router";
import { lazy, Suspense, useEffect, useRef, useState, type ReactNode } from "react";
import {
  Phone,
  KeyRound,
  Home as HomeIcon,
  Car,
  Clock,
  ShieldCheck,
  Zap,
  Award,
  MapPin,
  Mail,
  Menu,
  X,
  Check,
  ArrowRight,
} from "@/lib/icons";
import {
  LOGO_380,
  LOGO_512,
  LOGO_HERO,
  LOGO_MARK,
  MAPS_EMBED,
  MAPS_LINK,
  SITE_URL,
} from "@/lib/site";

const ContactForm = lazy(() =>
  import("@/components/ContactForm").then((m) => ({ default: m.ContactForm })),
);

const PHONE_DISPLAY = "(808) 456-1235";
const PHONE_HREF = "tel:8084561235";
const LOGO_ALT = "Jack's Lock Service locksmith logo — Mililani, HI";

const TITLE = "24/7 Emergency Locksmith Mililani, HI | Jack's Lock Service";
const DESCRIPTION =
  "Locked out? Jack's Lock Service is your 24/7 emergency locksmith in Mililani & Central Oahu — car lockouts, home lock solutions & car key replacement. Call (808) 456-1235.";

export const Route = createFileRoute("/")({
  component: Index,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { name: "robots", content: "index, follow" },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: SITE_URL + "/" },
      { property: "og:image", content: SITE_URL + LOGO_HERO },
      { property: "og:image:alt", content: LOGO_ALT },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESCRIPTION },
      { name: "twitter:image", content: SITE_URL + LOGO_HERO },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Locksmith",
          name: "Jack's Lock Service",
          description: DESCRIPTION,
          url: SITE_URL + "/",
          image: SITE_URL + LOGO_HERO,
          telephone: "+1-808-456-1235",
          email: "jack@jackslockservice.com",
          address: {
            "@type": "PostalAddress",
            streetAddress: "95-1003 Lalai Street",
            addressLocality: "Mililani",
            addressRegion: "HI",
            postalCode: "96789",
            addressCountry: "US",
          },
          hasMap: MAPS_LINK,
          geo: { "@type": "GeoCoordinates", latitude: 21.4389, longitude: -158.0111 },
          areaServed: [
            "Mililani, HI",
            "Waipio Acres, HI",
            "Pearl City, HI",
            "Wahiawa, HI",
            "Waipahu, HI",
            "Central Oahu, HI",
          ],
          openingHoursSpecification: [
            {
              "@type": "OpeningHoursSpecification",
              dayOfWeek: [
                "Monday",
                "Tuesday",
                "Wednesday",
                "Thursday",
                "Friday",
                "Saturday",
                "Sunday",
              ],
              opens: "00:00",
              closes: "23:59",
            },
          ],
        }),
      },
    ],
  }),
});

const NAV = [
  { label: "Home", href: "#home", id: "home" },
  { label: "Services", href: "#services", id: "services" },
  { label: "About", href: "#about", id: "about" },
  { label: "Contact", href: "#contact", id: "contact" },
];

const SERVICES = [
  {
    n: "01",
    icon: KeyRound,
    title: "Emergency Lockout Service",
    body: "Locked out of your car, house, or business? We respond fast, 24 hours a day, 7 days a week, and get you back inside without damage.",
  },
  {
    n: "02",
    icon: HomeIcon,
    title: "Residential Lock Solutions",
    body: "Rekeying, lock repair, replacement, and new deadbolt installation to keep your home secure.",
  },
  {
    n: "03",
    icon: Car,
    title: "Car Key Replacement",
    body: "Lost or broken car keys replaced on the spot, including key cutting and fob programming for most makes and models.",
  },
];

const STATS = [
  {
    icon: Clock,
    value: "24/7",
    label: "Always Open",
    body: "Emergencies don't keep business hours — neither do we.",
  },
  { icon: Zap, value: "Fast", label: "Response", body: "We answer immediately and head your way." },
  {
    icon: ShieldCheck,
    value: "100%",
    label: "Damage-Free Methods",
    body: "Professional entry techniques, no broken locks.",
  },
  {
    icon: Award,
    value: "Oahu",
    label: "Locally Owned",
    body: "Based in Mililani, serving Central Oahu.",
  },
];

const STEPS = [
  { n: "1", icon: Phone, text: `Call ${PHONE_DISPLAY} any time, day or night.` },
  { n: "2", icon: Phone, text: "We answer immediately and head your way." },
  { n: "3", icon: KeyRound, text: "You're back in, fast." },
];

const TAGS = ["Locally Owned", "Experienced", "Professional", "Central Oahu"];
const AREAS = ["Mililani", "Waipio Acres", "Pearl City", "Wahiawa", "Waipahu", "Central Oahu"];
const BADGES = [
  "24/7 Availability",
  "Licensed & Experienced",
  "Fast Response",
  "Serving Central Oahu",
];

/* ---------- motion helpers ---------- */

function useReveal() {
  useEffect(() => {
    const els = Array.from(document.querySelectorAll<HTMLElement>(".reveal"));
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            e.target.classList.add("in");
            io.unobserve(e.target);
          }
        }
      },
      { rootMargin: "0px 0px -10% 0px", threshold: 0.1 },
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);
}

function useActiveSection() {
  const [active, setActive] = useState("home");
  useEffect(() => {
    const onScroll = () => {
      let current = "home";
      for (const item of NAV) {
        const el = document.getElementById(item.id);
        if (el && el.getBoundingClientRect().top <= 140) current = item.id;
      }
      setActive(current);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return active;
}

function useScrolled() {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return scrolled;
}

function Reveal({
  children,
  delay = 0,
  className = "",
  as: Tag = "div",
}: {
  children: ReactNode;
  delay?: number;
  className?: string;
  as?: "div" | "article" | "li" | "section";
}) {
  return (
    <Tag className={`reveal ${className}`} style={{ ["--reveal-delay" as string]: `${delay}ms` }}>
      {children}
    </Tag>
  );
}

/* ---------- decorative brand art ---------- */

function Keyhole({ className = "" }: { className?: string }) {
  return (
    <svg viewBox="0 0 100 140" aria-hidden="true" className={className}>
      <path
        d="M50 6c-19 0-34 15-34 34 0 12 6 22 15 28l-11 60h60l-11-60c9-6 15-16 15-28C84 21 69 6 50 6z"
        fill="currentColor"
      />
    </svg>
  );
}

function SpeedLines({ className = "" }: { className?: string }) {
  return (
    <svg viewBox="0 0 200 120" aria-hidden="true" className={className}>
      {[18, 44, 70, 96].map((y, i) => (
        <rect
          key={y}
          x={i * 16}
          y={y}
          width={180 - i * 34}
          height={i % 2 ? 5 : 8}
          rx={4}
          fill="currentColor"
          opacity={0.85 - i * 0.15}
        />
      ))}
    </svg>
  );
}

function BigKey({ className = "" }: { className?: string }) {
  return (
    <svg viewBox="0 0 240 90" aria-hidden="true" className={className}>
      <circle cx="45" cy="45" r="34" fill="none" stroke="currentColor" strokeWidth="12" />
      <path
        d="M79 45h140M196 45v22M170 45v18M144 45v14"
        stroke="currentColor"
        strokeWidth="12"
        strokeLinecap="round"
        fill="none"
      />
    </svg>
  );
}

function Eyebrow({ children, light = false }: { children: ReactNode; light?: boolean }) {
  return (
    <p
      className={`flex items-center gap-3 font-display text-xs tracking-[0.22em] uppercase ${
        light ? "text-gold" : "text-gold-text"
      }`}
    >
      <span className="h-px w-8 bg-gold" aria-hidden="true" />
      {children}
    </p>
  );
}

function CallButton({ className = "" }: { className?: string }) {
  return (
    <a
      href={PHONE_HREF}
      className={`shine inline-flex items-center justify-center gap-2 rounded-xl bg-gold px-5 py-3 font-display text-sm tracking-wide text-gold-foreground uppercase shadow-[var(--shadow-card)] transition-transform duration-200 hover:-translate-y-0.5 ${className}`}
    >
      <Phone className="size-4 shrink-0" aria-hidden="true" />
      Call {PHONE_DISPLAY}
    </a>
  );
}

function MapEmbed() {
  const [showMap, setShowMap] = useState(false);

  if (showMap) {
    return (
      <iframe
        title="Map of Jack's Lock Service, 95-1003 Lalai Street, Mililani, Hawaii"
        src={MAPS_EMBED}
        className="h-72 w-full"
        loading="eager"
        referrerPolicy="no-referrer-when-downgrade"
      />
    );
  }

  return (
    <button
      type="button"
      onClick={() => setShowMap(true)}
      className="flex h-72 w-full flex-col items-center justify-center gap-3 bg-gold-soft text-ink"
      aria-label="Load map of Jack's Lock Service in Mililani, Hawaii"
    >
      <MapPin className="size-10 text-gold-text" aria-hidden="true" />
      <span className="font-display text-lg tracking-wide uppercase">View map</span>
      <span className="text-sm text-muted-foreground">95-1003 Lalai Street, Mililani, HI</span>
      <span className="text-xs font-semibold text-gold-text">Tap to load Google Maps</span>
    </button>
  );
}

function ContactFormPlaceholder() {
  return (
    <div
      className="min-h-[28rem] rounded-3xl border border-gold/25 bg-ink-foreground/5 p-6 sm:p-8"
      aria-hidden="true"
    />
  );
}

function DeferredContactForm() {
  const ref = useRef<HTMLDivElement>(null);
  const [load, setLoad] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setLoad(true);
          io.disconnect();
        }
      },
      { rootMargin: "240px" },
    );
    io.observe(node);
    return () => io.disconnect();
  }, []);

  return (
    <div ref={ref}>
      {load ? (
        <Suspense fallback={<ContactFormPlaceholder />}>
          <ContactForm />
        </Suspense>
      ) : (
        <ContactFormPlaceholder />
      )}
    </div>
  );
}

function Index() {
  const [open, setOpen] = useState(false);
  const active = useActiveSection();
  const scrolled = useScrolled();
  useReveal();
  const heroRef = useRef<HTMLDivElement>(null);

  return (
    <div className="min-h-screen overflow-x-clip bg-background text-foreground">
      <a href="#home" className="skip-link">
        Skip to content
      </a>
      <header
        className={`sticky top-0 z-50 transition-all duration-300 ${
          scrolled
            ? "border-b border-border bg-background/95 shadow-[0_8px_24px_-18px_oklch(0.2_0_0/45%)] backdrop-blur"
            : "border-b border-transparent bg-transparent"
        }`}
      >
        <div className="mx-auto grid max-w-6xl grid-cols-[minmax(0,1fr)_auto] items-center gap-3 px-4 py-3 lg:flex lg:justify-between">
          <a href="#home" className="flex min-w-0 items-center gap-2">
            <img
              src={LOGO_MARK}
              alt=""
              className="cutout h-12 w-12 shrink-0 object-contain sm:h-14 sm:w-14"
              width={56}
              height={56}
              decoding="async"
              fetchPriority="low"
            />
            <span className="min-w-0">
              <span className="block truncate font-display text-lg leading-none sm:text-xl">
                Jack's Lock Service
              </span>
              <span className="block text-[11px] font-medium tracking-widest text-gold-text uppercase">
                Mililani · Open 24/7
              </span>
            </span>
          </a>

          <nav aria-label="Main" className="hidden items-center gap-7 lg:flex">
            {NAV.map((item) => (
              <a
                key={item.href}
                href={item.href}
                aria-current={active === item.id ? "page" : undefined}
                className={`relative font-display text-sm tracking-wide uppercase transition-colors after:absolute after:-bottom-1.5 after:left-0 after:h-0.5 after:bg-gold after:transition-all after:duration-300 hover:text-gold-text ${
                  active === item.id ? "text-gold-text after:w-full" : "after:w-0"
                }`}
              >
                {item.label}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <a
              href={PHONE_HREF}
              className="shine inline-flex items-center gap-2 rounded-xl bg-gold px-3 py-2.5 font-display text-xs tracking-wide text-gold-foreground uppercase transition-transform duration-200 hover:-translate-y-0.5 sm:px-4 sm:text-sm"
            >
              <Phone className="size-4 shrink-0" aria-hidden="true" />
              <span className="hidden sm:inline">Call {PHONE_DISPLAY}</span>
              <span className="sm:hidden">Call</span>
            </a>
            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              aria-label={open ? "Close menu" : "Open menu"}
              aria-expanded={open}
              aria-controls="mobile-menu"
              className="inline-flex size-10 items-center justify-center rounded-xl border border-border bg-background/80 lg:hidden"
            >
              {open ? (
                <X className="size-5" aria-hidden="true" />
              ) : (
                <Menu className="size-5" aria-hidden="true" />
              )}
            </button>
          </div>
        </div>

        {open && (
          <nav
            id="mobile-menu"
            aria-label="Mobile"
            className="border-t border-border bg-background lg:hidden"
          >
            <ul className="mx-auto max-w-6xl px-4 py-2">
              {NAV.map((item) => (
                <li key={item.href}>
                  <a
                    href={item.href}
                    onClick={() => setOpen(false)}
                    className="block border-b border-border/60 py-3 font-display text-sm tracking-wide uppercase last:border-0"
                  >
                    {item.label}
                  </a>
                </li>
              ))}
            </ul>
          </nav>
        )}
      </header>

      <main className="pb-24 lg:pb-0">
        {/* Hero */}
        <section id="home" className="relative scroll-mt-24 overflow-hidden bg-gold-soft/60">
          <BigKey className="pointer-events-none absolute -top-10 -left-24 w-[34rem] text-gold/10" />
          <Keyhole className="pointer-events-none absolute -right-10 bottom-24 h-72 text-ink/5" />
          <div
            ref={heroRef}
            className="relative mx-auto grid max-w-6xl items-center gap-10 px-4 pt-12 pb-24 sm:pt-16 lg:grid-cols-[1.1fr_1fr] lg:pb-28"
          >
            <div className="relative lg:col-start-2 lg:row-start-1">
              <SpeedLines className="pointer-events-none absolute top-1/2 -left-8 w-40 -translate-y-1/2 text-gold/70 sm:w-52" />
              <picture>
                <source media="(min-width: 1024px)" srcSet={LOGO_512} type="image/webp" />
                <img
                  src={LOGO_380}
                  alt={LOGO_ALT}
                  className="cutout relative mx-auto aspect-square w-full max-w-md object-contain lg:max-w-lg"
                  width={380}
                  height={380}
                  fetchPriority="high"
                  loading="eager"
                  decoding="sync"
                />
              </picture>
              <span className="animate-floaty absolute top-4 -left-2 inline-flex items-center whitespace-nowrap rounded-full border border-gold/40 bg-card px-4 py-2 text-xs leading-none font-semibold tracking-wide shadow-[var(--shadow-card)] sm:left-2">
                24/7 Emergency Response
              </span>
              <span
                className="animate-floaty absolute right-0 -bottom-2 inline-flex items-center whitespace-nowrap rounded-full border border-gold/40 bg-card px-4 py-2 text-xs leading-none font-semibold tracking-wide shadow-[var(--shadow-card)]"
                style={{ animationDelay: "1.4s" }}
              >
                Central Oahu's Locksmith
              </span>
            </div>
            <Reveal className="lg:col-start-1 lg:row-start-1">
              <p className="mb-4 inline-flex items-center gap-2 rounded-full bg-ink px-3 py-1 text-xs font-semibold tracking-widest text-ink-foreground uppercase">
                <span className="size-2 animate-pulse rounded-full bg-gold" aria-hidden="true" />
                Open 24 hours · 7 days
              </p>
              <h1 className="text-4xl leading-[1.05] sm:text-5xl lg:text-6xl">
                24/7 Emergency Locksmith in <span className="accent-word">Mililani</span> &amp;
                Central Oahu
              </h1>
              <p className="mt-5 max-w-xl text-base text-muted-foreground sm:text-lg">
                Locked out of your car, home, or business? Jack's Lock Service gets you back in fast
                — day or night. Experienced, professional, and always answering the phone.
              </p>
              <div className="mt-7 flex flex-col gap-3 sm:flex-row">
                <a
                  href={PHONE_HREF}
                  className="shine inline-flex items-center justify-center gap-2 rounded-2xl bg-gold px-6 py-4 font-display text-base tracking-wide text-gold-foreground uppercase shadow-[var(--shadow-lift)] transition-transform duration-200 hover:-translate-y-1"
                >
                  <Phone className="size-5" aria-hidden="true" />
                  Call Now — {PHONE_DISPLAY}
                </a>
                <a
                  href="#services"
                  className="inline-flex items-center justify-center rounded-2xl border-2 border-ink px-6 py-4 font-display text-base tracking-wide uppercase transition-all duration-200 hover:-translate-y-1 hover:bg-ink hover:text-ink-foreground"
                >
                  View Our Services
                </a>
              </div>
              <ul className="mt-7 flex flex-wrap gap-x-7 gap-y-3">
                {["No Damage Entry", "Fast Response", "A Real Person Answers"].map((t) => (
                  <li key={t} className="flex items-center gap-2 text-sm font-semibold">
                    <span className="inline-flex size-5 items-center justify-center rounded-full bg-gold/20 text-gold">
                      <Check className="size-3.5" aria-hidden="true" />
                    </span>
                    {t}
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>

          {/* dark badge bar */}
          <div className="relative bg-ink py-4 text-ink-foreground">
            <div className="mx-auto hidden max-w-6xl flex-wrap items-center justify-center gap-x-8 gap-y-2 px-4 sm:flex">
              {BADGES.map((b) => (
                <span
                  key={b}
                  className="flex items-center gap-2 font-display text-xs tracking-widest uppercase"
                >
                  <ShieldCheck className="size-4 text-gold" aria-hidden="true" />
                  {b}
                </span>
              ))}
            </div>
            <div className="overflow-hidden sm:hidden">
              <div className="animate-marquee flex w-max gap-8 pr-8">
                {[...BADGES, ...BADGES].map((b, i) => (
                  <span
                    key={`${b}-${i}`}
                    className="flex shrink-0 items-center gap-2 font-display text-xs tracking-widest uppercase"
                  >
                    <ShieldCheck className="size-4 text-gold" aria-hidden="true" />
                    {b}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Services */}
        <section id="services" className="relative scroll-mt-24 overflow-hidden py-16 sm:py-20">
          <Keyhole className="pointer-events-none absolute -top-16 right-1/4 h-64 text-gold/5" />
          <div className="relative mx-auto max-w-6xl px-4">
            <Reveal>
              <Eyebrow>Locksmith Services</Eyebrow>
              <h2 className="mt-3 text-3xl sm:text-4xl">
                <span className="accent-word">Locksmith</span> Services on Oahu
              </h2>
              <div className="gold-rule mt-4" />
            </Reveal>
            <div className="mt-10 grid gap-6 md:grid-cols-3">
              {SERVICES.map((s, i) => (
                <Reveal as="article" key={s.title} delay={i * 120}>
                  <div className="group relative flex h-full flex-col overflow-hidden rounded-2xl border border-border border-t-2 border-t-gold bg-card p-6 shadow-[var(--shadow-card)] transition-all duration-300 hover:-translate-y-2 hover:shadow-[var(--shadow-lift)]">
                    <Keyhole className="pointer-events-none absolute -right-6 -bottom-8 h-40 text-gold/[0.07]" />
                    <span className="pointer-events-none absolute top-4 right-5 font-display text-4xl text-gold/25">
                      {s.n}
                    </span>
                    <span className="mb-4 inline-flex size-12 items-center justify-center rounded-xl bg-ink text-gold transition-colors duration-300 group-hover:bg-gold group-hover:text-gold-foreground">
                      <s.icon className="size-6" aria-hidden="true" />
                    </span>
                    <h3 className="relative text-xl">{s.title}</h3>
                    <p className="relative mt-3 flex-1 text-sm leading-relaxed text-muted-foreground">
                      {s.body}
                    </p>
                    <a
                      href={PHONE_HREF}
                      className="relative mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-gold-text underline-offset-4 hover:underline"
                    >
                      <Phone className="size-4" aria-hidden="true" />
                      Call for fast service
                      <ArrowRight
                        className="size-4 -translate-x-2 opacity-0 transition-all duration-300 group-hover:translate-x-0 group-hover:opacity-100"
                        aria-hidden="true"
                      />
                    </a>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        <div className="key-teeth mx-auto max-w-6xl px-4" aria-hidden="true" />

        {/* Why choose us */}
        <section className="relative overflow-hidden border-y border-border bg-muted py-16 sm:py-20">
          <BigKey className="pointer-events-none absolute -right-20 bottom-6 w-[30rem] text-gold/10" />
          <div className="relative mx-auto grid max-w-6xl gap-10 px-4 lg:grid-cols-[0.85fr_1.15fr]">
            <Reveal>
              <Eyebrow>Why Jack's</Eyebrow>
              <h2 className="mt-3 text-3xl sm:text-4xl">
                Why Central Oahu Calls <span className="accent-word">Jack's</span>
              </h2>
              <div className="gold-rule mt-4" />
              <p className="mt-6 text-base leading-relaxed text-muted-foreground">
                When you're stuck outside your car or front door, you want one call and a clear
                answer. We pick up the phone ourselves, tell you what it takes, and get to work with
                methods that protect your locks and your property.
              </p>
            </Reveal>
            <div className="grid gap-5 sm:grid-cols-2">
              {STATS.map((s, i) => (
                <Reveal key={s.value + s.label} delay={i * 110}>
                  <div className="group relative h-full overflow-hidden rounded-2xl border border-border bg-card p-6 shadow-[var(--shadow-card)] transition-all duration-300 hover:-translate-y-1.5 hover:shadow-[var(--shadow-lift)]">
                    <span
                      className="absolute top-0 right-0 size-10 rounded-bl-2xl bg-gold/20 transition-colors duration-300 group-hover:bg-gold/40"
                      aria-hidden="true"
                    />
                    <s.icon className="size-7 text-gold" aria-hidden="true" />
                    <p className="mt-3 font-display text-4xl leading-none">{s.value}</p>
                    <h3 className="mt-2 text-sm tracking-widest text-gold-text">{s.label}</h3>
                    <p className="mt-2 text-sm text-muted-foreground">{s.body}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
          <Reveal className="relative mx-auto mt-12 max-w-4xl px-4" delay={150}>
            <p className="rounded-2xl border border-gold/40 bg-card px-6 py-8 text-center font-accent text-2xl italic shadow-[var(--shadow-card)] sm:text-3xl">
              "No shortcuts. No damage. No callbacks."
            </p>
          </Reveal>
        </section>

        {/* How it works */}
        <section className="relative overflow-hidden py-16 sm:py-20">
          <div className="mx-auto max-w-6xl px-4">
            <Reveal>
              <Eyebrow>The Process</Eyebrow>
              <h2 className="mt-3 text-3xl sm:text-4xl">
                How It <span className="accent-word">Works</span>
              </h2>
              <div className="gold-rule mt-4" />
            </Reveal>
            <div className="relative mt-12">
              <span
                className="pointer-events-none absolute top-8 left-6 hidden h-0.5 w-[calc(100%-3rem)] border-t-2 border-dashed border-gold/50 md:block"
                aria-hidden="true"
              />
              <span
                className="pointer-events-none absolute top-6 left-6 h-[calc(100%-3rem)] border-l-2 border-dashed border-gold/50 md:hidden"
                aria-hidden="true"
              />
              <span
                className="animate-travel pointer-events-none absolute top-4 hidden text-gold md:block"
                aria-hidden="true"
              >
                <KeyRound className="size-6" />
              </span>
              <ol className="grid gap-6 md:grid-cols-3">
                {STEPS.map((s, i) => (
                  <Reveal as="li" key={s.n} delay={i * 140} className="relative">
                    <div className="group h-full rounded-2xl border border-border bg-card p-6 pt-8 shadow-[var(--shadow-card)] transition-all duration-300 hover:-translate-y-2 hover:shadow-[var(--shadow-lift)]">
                      <div className="flex items-center gap-4">
                        <span className="stroke-gold font-display text-5xl leading-none">
                          {s.n}
                        </span>
                        <s.icon
                          className={`size-6 text-gold ${i === 1 ? "animate-ringing" : ""}`}
                          aria-hidden="true"
                        />
                      </div>
                      <p className="mt-4 font-semibold">{s.text}</p>
                    </div>
                  </Reveal>
                ))}
              </ol>
            </div>
          </div>
        </section>

        {/* About */}
        <section
          id="about"
          className="relative scroll-mt-24 overflow-hidden border-t border-border bg-gold-soft/50 py-16 sm:py-20"
        >
          <Keyhole className="pointer-events-none absolute -left-16 top-10 h-80 text-ink/5" />
          <div className="relative mx-auto grid max-w-6xl items-center gap-12 px-4 lg:grid-cols-[1fr_0.8fr]">
            <Reveal>
              <Eyebrow>About Us</Eyebrow>
              <h2 className="mt-3 text-3xl sm:text-4xl">
                About <span className="accent-word">Jack's</span> Lock Service
              </h2>
              <div className="gold-rule mt-4" />
              <p className="mt-6 text-base leading-relaxed text-muted-foreground">
                Jack's Lock Service is a locally owned locksmith based in Mililani, serving Waipio
                Acres, Pearl City, Wahiawa, and communities across Central Oahu. With years of
                hands-on experience, we handle every lockout, rekey, and key replacement with
                professional care — and we take real pride in showing up fast and doing the job
                right the first time. When you call, a real person answers and help is already on
                the way.
              </p>
              <ul className="mt-6 flex flex-wrap gap-2">
                {TAGS.map((t) => (
                  <li
                    key={t}
                    className="rounded-full border border-ink/15 bg-card px-3 py-1.5 text-xs font-semibold tracking-wide uppercase shadow-[var(--shadow-card)]"
                  >
                    {t}
                  </li>
                ))}
              </ul>
              <p className="mt-8 font-display text-sm tracking-wide">Service Area</p>
              <ul className="mt-3 flex flex-wrap gap-2">
                {AREAS.map((a) => (
                  <li
                    key={a}
                    className="inline-flex items-center gap-1.5 rounded-xl border border-gold/40 bg-card px-3 py-1.5 text-xs font-medium"
                  >
                    <MapPin className="size-3.5 text-gold" aria-hidden="true" />
                    {a}
                  </li>
                ))}
              </ul>
              <CallButton className="mt-8" />
            </Reveal>
            <Reveal delay={120} className="relative">
              <div className="relative mx-auto max-w-sm rounded-3xl border-2 border-gold bg-card p-5 pb-14 shadow-[var(--shadow-lift)] lg:-translate-y-6">
                <img
                  src={LOGO_380}
                  alt=""
                  className="cutout mx-auto w-full object-contain"
                  width={380}
                  height={380}
                  loading="lazy"
                  decoding="async"
                />
                <span className="absolute bottom-4 left-1/2 -translate-x-1/2 rounded-full bg-ink px-4 py-2 text-center text-[11px] font-semibold tracking-widest text-ink-foreground uppercase">
                  Jack's Lock Service — Mililani, HI
                </span>
              </div>
            </Reveal>
          </div>
        </section>

        {/* Contact */}
        <section
          id="contact"
          className="relative scroll-mt-24 overflow-hidden bg-ink pt-16 pb-28 text-ink-foreground sm:pt-20"
        >
          <Keyhole className="pointer-events-none absolute -top-16 left-1/2 h-[30rem] -translate-x-1/2 text-gold/[0.07]" />
          <div className="relative mx-auto max-w-6xl px-4">
            <Reveal>
              <Eyebrow light>Contact</Eyebrow>
              <h2 className="mt-3 text-3xl text-ink-foreground sm:text-5xl">
                Locked Out? <span className="accent-word">Call Now.</span>
              </h2>
              <div className="gold-rule mt-4" />
              <a
                href={PHONE_HREF}
                className="mt-8 inline-block font-display text-4xl text-gold transition-transform duration-200 hover:scale-[1.02] sm:text-6xl"
              >
                {PHONE_DISPLAY}
              </a>
              <div className="mt-6">
                <a
                  href={PHONE_HREF}
                  className="animate-soft-pulse relative inline-flex items-center gap-2 rounded-2xl bg-gold px-6 py-4 font-display text-base tracking-wide text-gold-foreground uppercase"
                >
                  <Phone className="size-5" aria-hidden="true" />
                  Call {PHONE_DISPLAY}
                </a>
              </div>
              <p className="mt-6 flex items-center gap-2 text-sm text-ink-foreground/80">
                <span className="relative flex size-2.5" aria-hidden="true">
                  <span className="absolute inline-flex size-2.5 animate-ping rounded-full bg-green-400/70" />
                  <span className="relative inline-flex size-2.5 rounded-full bg-green-400" />
                </span>
                Available right now — call and a real person answers
              </p>
            </Reveal>
            <div className="mt-10 grid gap-6 sm:grid-cols-3">
              {[
                {
                  icon: MapPin,
                  content: (
                    <span>
                      95-1003 Lalai Street
                      <br />
                      Mililani, Hawaii 96789
                    </span>
                  ),
                },
                {
                  icon: Mail,
                  content: (
                    <a href="mailto:jack@jackslockservice.com" className="hover:text-gold">
                      jack@jackslockservice.com
                    </a>
                  ),
                },
                { icon: Clock, content: <span>Open 24 Hours, 7 Days a Week</span> },
              ].map((item, i) => (
                <Reveal key={i} delay={i * 110}>
                  <p className="flex items-start gap-3 rounded-2xl border border-gold/25 bg-ink-foreground/5 p-4 text-sm">
                    <item.icon className="mt-0.5 size-5 shrink-0 text-gold" aria-hidden="true" />
                    {item.content}
                  </p>
                </Reveal>
              ))}
            </div>
            <Reveal className="mt-10" delay={120}>
              <DeferredContactForm />
            </Reveal>
          </div>
          <Reveal className="relative mx-auto mt-12 max-w-6xl px-4" delay={100}>
            <div className="-mb-24 overflow-hidden rounded-3xl border-2 border-gold/50 shadow-[var(--shadow-lift)]">
              <MapEmbed />
            </div>
            <p className="mt-3 text-center">
              <a
                href={MAPS_LINK}
                className="text-sm font-semibold text-gold-text underline-offset-4 hover:underline"
                rel="noopener noreferrer"
                target="_blank"
              >
                Open in Google Maps
              </a>
            </p>
          </Reveal>
        </section>
      </main>

      <footer className="border-t border-border pt-32 pb-12">
        <div className="mx-auto grid max-w-6xl gap-8 px-4 sm:grid-cols-3">
          <div>
            <img
              src={LOGO_MARK}
              alt=""
              className="cutout h-20 w-20 object-contain"
              width={80}
              height={80}
              loading="lazy"
              decoding="async"
            />
            <p className="mt-3 font-display text-base">Jack's Lock Service</p>
            <p className="mt-1 text-sm text-muted-foreground">
              95-1003 Lalai Street, Mililani, Hawaii 96789
            </p>
            <p className="text-sm text-muted-foreground">{PHONE_DISPLAY}</p>
          </div>
          <nav aria-label="Footer">
            <h2 className="text-sm tracking-widest">Quick Links</h2>
            <ul className="mt-3 space-y-2">
              {NAV.map((item) => (
                <li key={item.href}>
                  <a
                    href={item.href}
                    className="text-sm text-muted-foreground hover:text-gold-text"
                  >
                    {item.label}
                  </a>
                </li>
              ))}
            </ul>
          </nav>
          <div>
            <h2 className="text-sm tracking-widest">24/7 Emergency Service</h2>
            <CallButton className="mt-3" />
          </div>
        </div>
        <p className="mx-auto mt-10 max-w-6xl px-4 text-xs text-muted-foreground">
          © {new Date().getFullYear()} Jack's Lock Service. All rights reserved.
        </p>
      </footer>

      {/* Sticky mobile call bar */}
      <a
        href={PHONE_HREF}
        className="animate-soft-pulse fixed inset-x-0 bottom-0 z-50 flex items-center justify-center gap-2 bg-gold py-4 pb-[max(1rem,env(safe-area-inset-bottom))] font-display text-base tracking-wide text-gold-foreground uppercase lg:hidden"
      >
        <Phone className="size-5" aria-hidden="true" />
        Call Now — {PHONE_DISPLAY}
      </a>
    </div>
  );
}
